/**
 * Server Clock Offset Tracking
 *
 * Request signatures are stamped with a timestamp and validated server-side
 * against a max-age window (see supabase/functions/_shared/requestValidation).
 * When the client machine's clock drifts, every signed request is rejected with
 * "Request expired. Age: <n>ms, Max: 120000ms" even though the request was just
 * made.
 *
 * To stay correct regardless of local clock accuracy, we learn the offset
 * between the server clock and the local clock from the `Date` header that
 * every HTTP response carries, and stamp signatures with the corrected time.
 */

/** Milliseconds to add to Date.now() to approximate the server's clock. */
let serverClockOffsetMs = 0;

/** Whether we have observed at least one server response. */
let hasObservedServerClock = false;

/**
 * Offsets below this are treated as noise (network latency, not real drift)
 * and ignored, so normal jitter doesn't cause the stamp to wobble.
 */
const MIN_SIGNIFICANT_OFFSET_MS = 2000;

/**
 * Record the server's clock from a response's `Date` header.
 *
 * The header has one-second resolution and the value was produced partway
 * through the round trip, so this is approximate — which is fine, since the
 * validation window it feeds is measured in minutes, not milliseconds.
 */
export function observeServerClock(headers: Headers): void {
  const dateHeader = headers.get('Date');
  if (!dateHeader) return;

  const serverTime = Date.parse(dateHeader);
  if (Number.isNaN(serverTime)) return;

  const observedOffset = serverTime - Date.now();

  // Ignore sub-threshold offsets so ordinary latency isn't mistaken for drift.
  if (Math.abs(observedOffset) < MIN_SIGNIFICANT_OFFSET_MS) {
    serverClockOffsetMs = 0;
    hasObservedServerClock = true;
    return;
  }

  serverClockOffsetMs = observedOffset;
  hasObservedServerClock = true;

  console.warn(
    `[requestSigning] Client clock differs from server by ${Math.round(
      observedOffset / 1000
    )}s. Compensating when signing requests.`
  );
}

/**
 * Current time, corrected toward the server's clock.
 * Falls back to the local clock until a server response has been seen.
 */
export function getSynchronizedNow(): number {
  return Date.now() + serverClockOffsetMs;
}

/** The offset currently applied, in milliseconds. Exposed for diagnostics. */
export function getServerClockOffsetMs(): number {
  return serverClockOffsetMs;
}

/** Whether a server response has been observed yet. */
export function hasServerClockReference(): boolean {
  return hasObservedServerClock;
}

/** Reset tracked state. Intended for tests. */
export function resetServerClockOffset(): void {
  serverClockOffsetMs = 0;
  hasObservedServerClock = false;
}

/**
 * Matches the server's expiry error, which reports exactly how stale it
 * considered the request:
 *   "Request expired. Age: 682595ms, Max: 120000ms"
 */
const EXPIRY_ERROR_PATTERN = /Request expired\.\s*Age:\s*(-?\d+)ms,\s*Max:\s*(\d+)ms/i;

export interface ExpiredRequestInfo {
  ageMs: number;
  maxAgeMs: number;
}

/**
 * Parse a server error message describing an expired request signature.
 * Returns null when the message is some other error.
 */
export function parseExpiredRequestError(
  message: unknown
): ExpiredRequestInfo | null {
  if (typeof message !== 'string') return null;

  const match = message.match(EXPIRY_ERROR_PATTERN);
  if (!match) return null;

  return { ageMs: parseInt(match[1], 10), maxAgeMs: parseInt(match[2], 10) };
}

/**
 * Correct the tracked offset using a rejection the server already explained.
 *
 * The reported age is the server's clock minus the timestamp we sent, so it
 * measures our drift directly — more precisely than the one-second-resolution
 * `Date` header. Applying it lets a retry succeed on a machine whose clock is
 * badly wrong, including on the first request of a session, before any
 * response has been observed.
 *
 * `offsetWhenSigned` is the offset that was in effect when the rejected
 * request was stamped. The corrected offset is absolute, not cumulative, so
 * this stays right even though the same response also fed
 * `observeServerClock()` — adding the age on top of an already-updated offset
 * would double-count the drift and push the retry into the future.
 */
export function applyExpiredRequestCorrection(
  info: ExpiredRequestInfo,
  offsetWhenSigned: number
): void {
  serverClockOffsetMs = offsetWhenSigned + info.ageMs;
  hasObservedServerClock = true;

  console.warn(
    `[requestSigning] Server rejected a request as ${Math.round(
      info.ageMs / 1000
    )}s old. Correcting clock offset to ${Math.round(
      serverClockOffsetMs / 1000
    )}s and retrying.`
  );
}
